/**
 * Two-step required-consent modal shown to a patient before they submit
 * their first order (or whenever consent versions change).
 *
 *   Step 1: Telehealth consent (asynchronous care, limitations, emergencies)
 *   Step 2: GLP-1 specific informed consent (side effects, off-label use)
 *
 * Each step requires the patient to type their full name and click Accept.
 * Backend records: signed_name, ip, timestamp, version. Order submission is
 * blocked at the backend until BOTH consents are signed at the current version.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, AlertTriangle, Stethoscope, Pill } from "lucide-react";
import API from "@/lib/api";
import { toast } from "sonner";

export const TELEHEALTH_VERSION = "2026.02";
export const GLP1_VERSION = "2026.02";

const TELEHEALTH_BODY = (
  <div className="space-y-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
    <p>I, the undersigned patient, voluntarily consent to receive medical care from GLPFlo through telehealth, including <b>asynchronous (store-and-forward) consultations</b> conducted via secure online questionnaire, email, and chat.</p>
    <p className="font-semibold">I understand that:</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li><b>Asynchronous care is not real-time.</b> A response from my prescriber typically takes 24–72 hours. I will not use this service for urgent, emergency, or life-threatening medical issues.</li>
      <li><b>For medical emergencies I will call 911 or go to the nearest emergency department.</b> GLPFlo does not provide emergency care, mental-health crisis services, or after-hours medical advice.</li>
      <li><b>Telehealth has limitations</b> — my prescriber cannot perform a physical exam, and clinical decisions rely on the accuracy and completeness of the information I provide.</li>
      <li><b>I am responsible for providing accurate health information</b>, including current medications, allergies, medical conditions, and any changes during treatment. Withholding information may result in unsafe treatment.</li>
      <li><b>My prescriber may decline to prescribe</b> if telehealth is determined to be inappropriate for my needs.</li>
      <li><b>My PHI is protected</b> under HIPAA. See our Privacy Policy and Notice of Privacy Practices for details on collection, use, and disclosure.</li>
      <li><b>I may withdraw consent</b> at any time by notifying GLPFlo in writing, after which I may continue care elsewhere.</li>
    </ul>
    <p>By signing below, I acknowledge that I have read, understood, and voluntarily agree to these terms.</p>
  </div>
);

const GLP1_BODY = (
  <div className="space-y-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
    <p>I voluntarily consent to be treated with a <b>GLP-1 (glucagon-like peptide-1) receptor agonist medication</b> — semaglutide and/or tirzepatide — for the purpose of weight management.</p>
    <p className="font-semibold">I understand and accept the following risks and limitations:</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li><b>Off-label use:</b> Compounded semaglutide / tirzepatide is <b>not FDA-approved</b>. The medications I receive are compounded by a state-licensed compounding pharmacy. They are pharmaceutically equivalent in active ingredient but are distinct from the FDA-approved brand-name products (Ozempic®, Wegovy®, Mounjaro®, Zepbound®).</li>
      <li><b>Common side effects</b> include nausea, vomiting, diarrhea, constipation, abdominal pain, decreased appetite, fatigue, headache, and injection-site reactions.</li>
      <li><b>Serious risks</b> may include pancreatitis (inflammation of the pancreas), gallbladder disease, kidney problems, severe hypoglycemia (low blood sugar) — especially if combined with diabetes medications — diabetic retinopathy complications, severe allergic reactions, and possible increased risk of thyroid C-cell tumors (the FDA-approved product carries a boxed warning).</li>
      <li><b>Contraindications:</b> I must NOT take this medication if I or any first-degree family member has a personal history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN 2). I must NOT take this medication if I am pregnant, breastfeeding, or planning pregnancy in the next 2 months. I must inform my prescriber if I have or have had pancreatitis, gallbladder disease, kidney disease, severe gastroparesis, an eating disorder, or any allergic reaction to GLP-1 medications.</li>
      <li><b>Drug interactions:</b> GLP-1 medications can affect absorption of other oral medications. I will inform my prescriber of all medications, supplements, and herbal products I take.</li>
      <li><b>Required follow-up:</b> I agree to report any concerning symptoms promptly and to attend scheduled check-ins. Continued treatment may be conditional on my response, side-effect tolerance, and weight progress.</li>
      <li><b>Weight loss results vary</b> and are not guaranteed. Sustainable weight management also requires nutrition, physical activity, and behavioral changes.</li>
      <li><b>No emergency contraception advice:</b> GLP-1 medications may reduce the effectiveness of oral contraceptives. I will use a non-oral contraception method while on this medication if pregnancy prevention is needed.</li>
    </ul>
    <p>By signing below, I confirm that my prescriber has had the opportunity to address my questions, that I have read and understood the above, and that I voluntarily accept these risks.</p>
  </div>
);

export default function ConsentFlowModal({ open, onComplete, onCancel }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signedTelehealth, setSignedTelehealth] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setName("");
      setSignedTelehealth(false);
    }
  }, [open]);

  const submit = async (kind, version) => {
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Please type your full name to sign");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/consent/sign", { type: kind, version, signed_name: name.trim() });
      if (kind === "telehealth") {
        setSignedTelehealth(true);
        setStep(2);
        setName("");
        toast.success("Telehealth consent signed");
      } else {
        toast.success("GLP-1 consent signed");
        onComplete?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record consent");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="consent-flow-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 1 ? <Stethoscope className="w-5 h-5 text-purple-600" /> : <Pill className="w-5 h-5 text-purple-600" />}
            {step === 1 ? "Telehealth Consent" : "GLP-1 Medication Informed Consent"}
            <span className="ml-auto text-xs font-medium text-slate-400">Step {step} of 2</span>
          </DialogTitle>
        </DialogHeader>

        <Alert className="mb-3 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-xs text-amber-900 dark:text-amber-200">
            This is a legally binding signature. Please read carefully — your typed name + the current time + your IP address are recorded as your signature.
          </AlertDescription>
        </Alert>

        {step === 1 ? TELEHEALTH_BODY : GLP1_BODY}

        <div className="mt-5 space-y-3 border-t border-slate-200 dark:border-slate-700 pt-4">
          <div>
            <Label htmlFor="consent-name" className="text-sm font-medium">Type your full legal name to sign</Label>
            <Input
              id="consent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First and last name"
              className="mt-1"
              data-testid={step === 1 ? "consent-telehealth-name" : "consent-glp1-name"}
              autoComplete="off"
            />
          </div>
          <div className="flex justify-between items-center gap-2 pt-1">
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
              data-testid="consent-cancel-btn"
            >Cancel</Button>
            <div className="flex items-center gap-2">
              {signedTelehealth && step === 2 && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Telehealth signed
                </span>
              )}
              <Button
                onClick={() => submit(step === 1 ? "telehealth" : "glp1", step === 1 ? TELEHEALTH_VERSION : GLP1_VERSION)}
                disabled={submitting || name.trim().length < 2}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid={step === 1 ? "consent-telehealth-sign-btn" : "consent-glp1-sign-btn"}
              >
                <ShieldCheck className="w-4 h-4 mr-1" />
                {submitting ? "Signing…" : step === 1 ? "Sign & continue" : "Sign & proceed to payment"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
